import {
  Component,
  OnInit,
  computed,
  inject,
  input,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { School } from '@awo-care/shared-core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { SchoolListComponent } from './schools-list.component';
import { SchoolService } from '@awo-care/schools';
import { groupSchoolsByCity } from './schools-grouping.utils';

@Component({
  selector: 'app-schools',
  standalone: true,
  imports: [
    SchoolListComponent,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    RouterLink,
  ],
  template: `
    <div class="page-container">
      <a class="back-link" routerLink="/care">
        <span aria-hidden="true">‹</span>
        <span i18n="@@schools.schools.backToOverview"
          >Zurück zur Übersicht</span
        >
      </a>

      <div class="header">
        @if (headingLevel() === 1) {
          <h1 id="schools-heading" i18n="@@schools.schools.participatingSchools">
            Teilnehmende Schulen
          </h1>
        } @else {
          <h2 id="schools-heading" i18n="@@schools.schools.participatingSchools">
            Teilnehmende Schulen
          </h2>
        }
        <p i18n="@@schools.schools.subtitle">
          Entdecken Sie alle Schulen, die an unserem Betreuungsprogramm
          teilnehmen
        </p>
      </div>

      <div class="search-container">
        <mat-form-field
          class="search-field"
          appearance="outline"
          color="accent"
        >
          <mat-label i18n="@@schools.schools.schoolOrCitySearch"
            >Schule oder Stadt suchen</mat-label
          >
          <mat-icon aria-hidden="true" matPrefix>search</mat-icon>
          <input matInput type="search" (input)="onSearch($event)" />
        </mat-form-field>
        @if (!loading()) {
        <div class="result-count">
          {{ filteredSchools().length }}
          <span i18n="@@schools.schools.resultCountLabel">Schulen</span>
        </div>
        }
      </div>
      <app-school-list
        [groups]="groupedSchools()"
        [loading]="loading()"
        [headingLevel]="headingLevel() === 1 ? 2 : 3"
      ></app-school-list>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .page-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        max-width: 1200px;
        margin: 0 auto;
        padding: 3rem 2rem;
      }

      .back-link {
        /*
         * Hidden by default: /care only exists in customer-ui. The website
         * app also renders this component (apps/website/src/app/home/home.component.ts
         * <app-schools /> + its own /schools route), where /care 404s.
         * Visibility is re-enabled below, scoped to customer-ui via
         * .awo-redesign on the app root (see apps/customer-ui/src/app/app.component.ts).
         */
        display: none;
        align-self: flex-start;
        align-items: center;
        gap: 0.35rem;
        margin-bottom: 1.5rem;
        color: var(--awo-text-subtle, #6f6a66);
        text-decoration: none;
        font-size: 0.9rem;
        font-weight: 500;

        span[aria-hidden] {
          font-size: 1.1rem;
          line-height: 1;
        }

        &:hover {
          color: var(--theme-primary, #e2382a);
        }
      }

      :host-context(.awo-redesign) .back-link {
        display: inline-flex;
      }

      .header {
        text-align: center;
        margin-bottom: 2.5rem;

        h2 {
          font-size: 2.2rem;
          font-weight: 500;
          color: #333;
          margin: 0 0 1rem 0;
          letter-spacing: -0.01em;

          @media (max-width: 768px) {
            font-size: 1.8rem;
          }
        }

        p {
          font-size: 1.1rem;
          color: var(--awo-text-muted, #6f6f6f);
          margin: 0;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.5;
          text-wrap: balance;
          font-weight: 400;

          @media (max-width: 768px) {
            font-size: 1rem;
          }
        }
      }

      .search-container {
        width: 100%;
        max-width: 600px;
        text-align: center;
        margin-bottom: 24px;
      }

      .search-field {
        width: 100%;
      }

      .result-count {
        margin-top: 0.5rem;
        font-size: 0.85rem;
        font-weight: 600;
        color: var(--awo-text-subtle, #6f6a66);
      }

      /*
       * Warm redesign look for the search field — scoped so it only applies
       * inside customer-ui (whose app root carries .awo-redesign, see
       * apps/customer-ui/src/app/app.component.ts). The website app also
       * renders this component (apps/website/src/app/home/home.component.ts
       * <app-schools /> + its own /schools route) and keeps the default
       * Material outline look untouched there.
       */
      :host-context(.awo-redesign) {
        ::ng-deep .search-field {
          .mat-mdc-text-field-wrapper {
            background: var(--awo-card);
            border-radius: var(--awo-radius-md);
          }

          .mdc-notched-outline__leading,
          .mdc-notched-outline__notch,
          .mdc-notched-outline__trailing {
            border-color: var(--awo-border) !important;
          }

          &.mat-focused .mdc-notched-outline__leading,
          &.mat-focused .mdc-notched-outline__notch,
          &.mat-focused .mdc-notched-outline__trailing {
            border-color: var(--theme-primary) !important;
          }
        }
      }
    `,
  ],
})
export class SchoolsComponent implements OnInit {
  /** s. HowItWorksComponent.headingLevel — Default 2 haelt customer-ui stabil. */
  readonly headingLevel = input<1 | 2>(2);

  private schoolService = inject(SchoolService);

  loading = signal(true);
  schools = signal<School[]>([]);
  searchTerm = signal('');

  filteredSchools = computed(() => {
    const term = this.searchTerm().toLowerCase();
    return this.schools().filter(
      (school) =>
        school.schoolName.toLowerCase().includes(term) ||
        school.address.city?.toLowerCase().includes(term) ||
        school.address.zipCode?.toLowerCase().includes(term)
    );
  });

  groupedSchools = computed(() => groupSchoolsByCity(this.filteredSchools()));

  async ngOnInit() {
    try {
      const schools = await this.schoolService.loadAllSchools();
      this.schools.set(schools);
    } catch (error) {
      console.error('Error loading schools:', error);
    } finally {
      this.loading.set(false);
    }
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
  }
}
